'use client'

import { useEffect, useState } from "react";
import { ServiceCard } from "@/app/Components/Element_Components/serviceCard/ServiceCard";
import TopHeader from "@/app/Components/Block_Components/topHeader/topHeaderBlock";
import Header from "@/app/Components/Block_Components/topHeader/header";
import NavBlock from "@/app/Components/Block_Components/navBlock/navBlock";
import HeroBlock from "@/app/Components/Block_Components/heroBlock/page";
import { NewsCard } from "../Components/Element_Components/newsCard/NewsCard";
import SlideBlock from "../Components/Block_Components/slideSection/SlideBlock";
import { useLanguage } from '@/app/LanguageContext';
import formatDate from '@/utils/formatDate';
import { cn } from "@/lib/utils";
import { Calendar } from "lucide-react";
import { Clock, Share2 } from 'lucide-react';
import Image from "next/image";
import Space from "@/app/Components/Element_Components/space/Space";
import { Badge } from "@/app/Components/Element_Components/badge/Badge";
import Link from "next/link";
import Breadcrumb from "../Components/Element_Components/breadcrumb/breadcrumbElement";
import { collectionLoader } from "@/utils/fetchCollection";
import { getPublicBaseUrl } from "@/lib/getBaseURL";


interface News {
  documentId: string
  category: string;
  headline: string;
  publishedAt: string;
  newsThumbnail: {
    url: string;
  };
}

const NewsPage = () => {
  const { language } = useLanguage();
  const [news, setNews] = useState<News[]>([]);
  const [latestNews, setLatestNews] = useState<News | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await collectionLoader<News>('news-contents', language);
        
        // Sort news by publishedAt date (newest first)
        const sortedNews = [...response].sort((a, b) => 
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        );
        
        setNews(sortedNews);
        setLatestNews(sortedNews[0]); // Set the first item as latest news
        setLoading(false);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news');
        setLoading(false);
      }
    }

    fetchData();
  }, [language]); // Added 'language' as a dependency

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!latestNews) {
    return <div className="text-center mt-20">No news found</div>;
  }


  return (
    <div className="min-h-screen space-y-5 mb-50">
      {/* <Breadcrumb links={breadcrumbProps}/> */}
      {/* <HeroBlock /> */}
      {/* <SlideBlock images={partnerLogos} autoplay={true} autoplayInterval={2000} /> */}
      
      {/* Latest News Section */}
      <div className="w-full h-[12rem] md:h-[42rem]">  
        <div 
          className="relative w-full xl:mx-auto  h-full bg-cover bg-no-repeat bg-center" 
          style={{
            backgroundImage: `url(${getPublicBaseUrl()}${latestNews?.newsThumbnail?.url})`
          }}
        >
          <div className="absolute inset-0 bg-black/45"></div>
          <div className="absolute inset-0">
            <Link href={`/newsDetail/${latestNews?.documentId}`}>
              <div className="xl:w-[71rem] mx-auto xl:pr-96 mt-19 md:mt-[26rem] lg:mt-[24rem] xl:mt-[22rem] w-[70%] h-fit md:h-[9.25rem] text-white gap-[0.625rem]">
                <h1 className='font-semibold md:font-bold text-sm md:text-[2rem] lg:text-[2rem] xl:text-[2.5rem] leading-[1.2] tracking-normal w-full'>
                  {latestNews?.headline}
                </h1>
                <div className="mb-2 md:text-sm text-xs items-center text-white mt-2 md:mt-5">
                  <Space size={12} >
                    <Badge className="bg-[#002B4E]">
                      {latestNews?.category?.toUpperCase()}
                    </Badge>
                    <Space align="center">
                      <span className='md:text-sm text-xs'><Clock className='p-1'/></span>
                      <span className="fw-light">
                        {formatDate(latestNews?.publishedAt)}
                      </span>
                    </Space>
                  </Space>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
      
      {/* All News Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12 max-w-[71rem] mx-auto mt-15 mb-20 px-5 xl:px-0">
        {news?.map((newsItem, index) => (
          // Skip the first item since it's already shown as the latest news
          index > 0 && (
            <NewsCard
              key={newsItem?.documentId}
              href={`/newsDetail/${newsItem?.documentId}`}
              featuredImage={`${getPublicBaseUrl()}${newsItem?.newsThumbnail?.url}`}
              category={newsItem?.category}
              title={newsItem?.headline}
              publishDate={formatDate(newsItem?.publishedAt)}
              className="w-full"
            />
          )
        ))}
      </div>
    </div>  
  );
};

export default NewsPage;